import { TFunction } from 'i18next';
import _get from 'lodash/get';
import { Values } from '@lib/interfaces/form';
import {
  AccessComponentEnum,
  AccessLevelEnum,
  CompanyPlanUpgradeFeatureEnum,
} from '@lib/graphql-common';
import UpgradePlanFieldNames from '@lib/enums/fieldNames/upgradePlanFieldNames';

export type GetUserAccessByCompany = (
  component: AccessComponentEnum,
) => AccessLevelEnum | undefined;

type FeatureOptionsArgs = {
  t: TFunction<'translation', undefined>;
  getUserAccessByCompany?: GetUserAccessByCompany;
};

export type UpgradePlanFeatureOption = {
  label: string;
  value: CompanyPlanUpgradeFeatureEnum;
};

// The subset of CompanyPlanUpgradeFeatureEnum that maps to a lockable
// company-plan access component; the "contact us" topics don't.
type LockableFeature =
  | CompanyPlanUpgradeFeatureEnum.Interventions
  | CompanyPlanUpgradeFeatureEnum.Documentation
  | CompanyPlanUpgradeFeatureEnum.AiAssistance;

// Maps each requestable feature to the company-plan access component that
// tells us whether it's still locked (AccessLevelEnum.LimitedRead) for the
// company, so already-unlocked features aren't offered again.
const FEATURE_ACCESS_COMPONENT: Record<LockableFeature, AccessComponentEnum> = {
  [CompanyPlanUpgradeFeatureEnum.Interventions]:
    AccessComponentEnum.InterventionsManagement,
  [CompanyPlanUpgradeFeatureEnum.Documentation]:
    AccessComponentEnum.DocumentationManagement,
  [CompanyPlanUpgradeFeatureEnum.AiAssistance]:
    AccessComponentEnum.AiAssistance,
};

// True once every lockable feature is already unlocked for the company —
// i.e. there's nothing left to request via the "upgrade plan" flow.
export function getAllFeaturesUnlocked({
  getUserAccessByCompany,
}: Pick<FeatureOptionsArgs, 'getUserAccessByCompany'>): boolean {
  if (!getUserAccessByCompany) return false;
  return Object.values(FEATURE_ACCESS_COMPONENT).every(
    (component) =>
      getUserAccessByCompany(component) !== AccessLevelEnum.LimitedRead,
  );
}

export function getUpgradePlanFeatureOptions({
  t,
  getUserAccessByCompany,
}: FeatureOptionsArgs): UpgradePlanFeatureOption[] {
  const allOptions = [
    {
      label: t('intervention-management'),
      value: CompanyPlanUpgradeFeatureEnum.Interventions,
    },
    {
      label: t('documentation-management'),
      value: CompanyPlanUpgradeFeatureEnum.Documentation,
    },
    {
      label: t('ai-assistance'),
      value: CompanyPlanUpgradeFeatureEnum.AiAssistance,
    },
  ];
  if (!getUserAccessByCompany) return allOptions;
  return allOptions.filter(
    ({ value }) =>
      getUserAccessByCompany(FEATURE_ACCESS_COMPONENT[value]) ===
      AccessLevelEnum.LimitedRead,
  );
}

// "Upgrade plan" flow select options: locked features plus User limit / Other.
export function getUpgradePlanSelectOptions({
  t,
  getUserAccessByCompany,
}: FeatureOptionsArgs): UpgradePlanFeatureOption[] {
  return [
    { label: t('user-limit'), value: CompanyPlanUpgradeFeatureEnum.UserLimit },
    ...getUpgradePlanFeatureOptions({ t, getUserAccessByCompany }),
    { label: t('other'), value: CompanyPlanUpgradeFeatureEnum.Other },
  ];
}

// "Contact our team" flow select options: request topics unrelated to any
// specific locked feature.
export function getContactUsTopicOptions({
  t,
}: Pick<FeatureOptionsArgs, 't'>): UpgradePlanFeatureOption[] {
  return [
    {
      label: t('plan-change'),
      value: CompanyPlanUpgradeFeatureEnum.PlanChange,
    },
    {
      label: t('user-limit-change'),
      value: CompanyPlanUpgradeFeatureEnum.UserLimitChange,
    },
    {
      label: t('billing-or-contract'),
      value: CompanyPlanUpgradeFeatureEnum.BillingOrContract,
    },
    {
      label: t('feature-question'),
      value: CompanyPlanUpgradeFeatureEnum.FeatureQuestion,
    },
    { label: t('other'), value: CompanyPlanUpgradeFeatureEnum.Other },
  ];
}

type FeatureSelectOptionsArgs = FeatureOptionsArgs & {
  isContactUsMode?: boolean;
};

// Resolves the "features" select options for whichever mode the modal is in.
export function getFeatureSelectOptions({
  t,
  getUserAccessByCompany,
  isContactUsMode,
}: FeatureSelectOptionsArgs): UpgradePlanFeatureOption[] {
  return isContactUsMode
    ? getContactUsTopicOptions({ t })
    : getUpgradePlanSelectOptions({ t, getUserAccessByCompany });
}

// True when the "User limit" option is among the selected features — the
// trigger for showing the "Desired user limit" input.
export function isUserLimitFeatureSelected(values: Values): boolean {
  return (
    (_get(
      values,
      UpgradePlanFieldNames.Features,
      [],
    ) as UpgradePlanFeatureOption[]) || []
  ).some(({ value }) => value === CompanyPlanUpgradeFeatureEnum.UserLimit);
}
